import {
  ResolvedScope, ResourceKind, ScopeBucket, ScopeRow
} from '@/types/apiKey';

// Asset types that have an Invoke surface
export const INVOKABLE_KINDS: ReadonlySet<ResourceKind> = new Set( [ 'constellation' ] );

const KIND_TO_BUCKET: Record<ResourceKind, keyof ResolvedScope> = {
  constellation: 'constellations',
  secret: 'secrets',
  evaluation: 'evaluations',
  api_key: 'api_keys'
};

//   - Write implies Read
//   - Write implies Invoke on invokable assets
//   - Read does not imply Invoke & Invoke does not imply Read
//   - No write only
export function normalizeRow( row: ScopeRow ): ScopeRow {
  const next = { ...row };
  if ( next.write ) {
    next.read = true;
    if ( INVOKABLE_KINDS.has( next.kind ) ) {
      next.invoke = true;
    }
  }
  return next;
}

export function rowHasAnyPermission( row: ScopeRow ): boolean {
  return row.read || row.invoke || row.write;
}

export function rowIsValid( row: ScopeRow ): boolean {
  // row with any permission checked must also have at least one target
  if ( !rowHasAnyPermission( row ) ) return true;
  return row.targets.length > 0;
}

export function rowsToResolvedScope(
  rows: ScopeRow[],
  opts: { mirror: boolean; fullAccess: boolean }
): ResolvedScope {
  const scope: ResolvedScope = {
    mirror: opts.mirror,
    full_access: opts.fullAccess
  };

  if ( opts.mirror || opts.fullAccess ) {
    // mirrored access hides individual scope chooser
    return scope;
  }

  rows.forEach( ( raw ) => {
    const row = normalizeRow( raw );
    if ( !rowHasAnyPermission( row ) || row.targets.length === 0 ) return;
    const bucketKey = KIND_TO_BUCKET[row.kind];
    const bucket: ScopeBucket = ( scope[bucketKey] as ScopeBucket ) ?? {};
    if ( row.read ) bucket.read = [ ...new Set( [ ...( bucket.read ?? [] ), ...row.targets ] ) ];
    if ( row.invoke && INVOKABLE_KINDS.has( row.kind ) ) {
      bucket.invoke = [ ...new Set( [ ...( bucket.invoke ?? [] ), ...row.targets ] ) ];
    }
    if ( row.write ) bucket.write = [ ...new Set( [ ...( bucket.write ?? [] ), ...row.targets ] ) ];
    ( scope as Record<string, unknown> )[bucketKey] = bucket;
  } );

  return scope;
}

export function emptyScopeRow( kind: ResourceKind ): ScopeRow {
  return { kind, read: false, invoke: false, write: false, targets: [] };
}
